import React from 'react';
import { has, assign, filter, pickBy, identity, isEmpty, isNil, reduce } from 'lodash';
import { Redirect } from 'react-router-dom';
import Form from '../elements/forms/Form';
import FormPage from '../elements/forms/FormPage';
import SelectConnection from '../data_sources/add_ds_form/SelectConnection';
import AuthenticateDataSource from '../data_sources/add_ds_form/AuthenticateDataSource';
import { withSnackbar } from 'js/utils/withSnackbar';
import DropboxService from '../../services/DropboxService';
import BoxService from '../../services/BoxService';
import SelectBucketContainer from '../data_sources/add_ds_form/SelectBucketContainer';
import {
  CONNECTION_CATEGORY_FILE,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
} from '../../constants/connectionCategories';
import { ACLQueryModeTypes } from '../../constants/visibilityFilters';

import { CONNECTORS_RESOURCE_TYPES } from '../../constants/resourceTypes';
import { FORM_TYPE_EDIT_CREDENTIAL, FORM_TYPE_EDIT_QUARANTINE } from '../../constants/formTypes';
import { ROUTE_ERROR_MANAGEMENT } from '../../constants/pathnames';
import { getEmptyRequiredFields } from '../../lib/ValidationUtils';
import { SUPPORT_KEY_QUARANTINE } from '../../constants/supportPageKeys';
import RoutePrefixContext from '../../../new/contexts/RoutePrefixContext';

class EditQuarantineForm extends React.Component {
  static contextType = RoutePrefixContext;

  constructor(props) {
    super(props);

    this.state = {
      selectedTemplate: null,
      selectedConnection: null,
      selectedCredential: null,
      selectedCredentialId: null,
      selectedBucket: null,
      credFormData: {},
      sourceConfig: {},
      awaitingProbeNewCred: false,
      redirectPath: null,
    };

    this.setInitialState = this.setInitialState.bind(this);
    this.redirectAfterCompletion = this.redirectAfterCompletion.bind(this);
    this.loadCredentials = this.loadCredentials.bind(this);
    this.validateSelectConnection = this.validateSelectConnection.bind(this);
    this.validateAuthenticate = this.validateAuthenticate.bind(this);
    this.validateConfigure = this.validateConfigure.bind(this);
    this.buildSelectedBucket = this.buildSelectedBucket.bind(this);
    this.handleConnectionSelected = this.handleConnectionSelected.bind(this);
    this.handleCredSelectionChanged = this.handleCredSelectionChanged.bind(this);
    this.handleCredentialFormInputChanged = this.handleCredentialFormInputChanged.bind(this);
    this.handleSourceConfigChanged = this.handleSourceConfigChanged.bind(this);
    this.onBucketSelected = this.onBucketSelected.bind(this);
    this.onRequestProbeTree = this.onRequestProbeTree.bind(this);
    this.onToggleNodeProbeTree = this.onToggleNodeProbeTree.bind(this);
    this.onNewDropboxCredential = this.onNewDropboxCredential.bind(this);
    this.onNewBoxCredential = this.onNewBoxCredential.bind(this);
  }
  componentDidMount() {
    this.loadCredentials();

    if (this.props.quarantineObj) {
      this.setInitialState();
    } else {
      this.props.loadUserQuarantine(this.props.userId);
    }

    this.props.setContextualSupport(SUPPORT_KEY_QUARANTINE);
  }

  componentWillUnmount() {
    this.props.unsetContextualSupport(SUPPORT_KEY_QUARANTINE);
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      this.props.quarantine &&
      this.props.quarantine.newCredentialId &&
      this.props.quarantine.validCredential
    ) {
      this.handleCredSelectionChanged(this.props.newCredential);
      this.props.setFormStageIndex(2);
      this.props.clearNewCredentialForQuarantine();
    }

    if (!prevProps.quarantineObj && this.props.quarantineObj) {
      this.setInitialState();
    }

    if (
      this.state.awaitingProbeNewCred &&
      this.state.selectedCredentialId !== prevState.selectedCredentialId &&
      this.props.probeStatus
    ) {
      this.boundValidateAuthenticate();
    }
  }
  setInitialState() {
    const {
      quarantineObj,
      credential,
      currentCredentialType,
      paging: { connAuthById },
    } = this.props;

    const { templates } = connAuthById[currentCredentialType] || {};
    const selectedTemplate = (() => {
      if (templates?.length > 1) {
        return templates[0].id;
      }
      return null;
    })();

    this.setState({
      selectedTemplate,
      sourceConfig: quarantineObj.config,
      selectedBucket: this.buildSelectedBucket(quarantineObj.config),
      selectedConnection: currentCredentialType,
      selectedCredential: credential,
      selectedCredentialId: credential.id,
      credFormData: {
        selectedCredentialId: credential.id,
        selectedCredentialInfo: credential,
      },
    });
  }
  redirectAfterCompletion() {
    this.setState({
      redirectPath: this.props.redirectPath || ROUTE_ERROR_MANAGEMENT,
    });
  }
  loadCredentials() {
    this.props.loadDataCredentials(ACLQueryModeTypes.MODE_COLLABORATOR.key);
  }

  onNewDropboxCredential(callback) {
    DropboxService.authenticate(callback, this.state.credFormData?.selectedCredentialInfo);
  }

  onNewBoxCredential(callback) {
    BoxService.authenticate(callback, this.state.credFormData?.selectedCredentialInfo);
  }

  validateSelectConnection(data, onSuccess, onError) {
    if (!this.state.selectedConnection) {
      onError('Please select a data connection.');
      return;
    }

    onSuccess();
  }
  validateAuthenticate(data, onSuccess, onError) {
    const { paging, quarantine, probeStatus } = this.props;
    const { selectedConnection, selectedCredentialId, credFormData, awaitingProbeNewCred } =
      this.state;

    this.boundValidateAuthenticate = this.validateAuthenticate.bind(this, ...arguments);

    if (awaitingProbeNewCred) {
      this.setState({
        awaitingProbeNewCred: false,
      });

      if (
        !(
          probeStatus &&
          selectedCredentialId &&
          probeStatus[selectedCredentialId] &&
          probeStatus[selectedCredentialId].status
        )
      ) {
        onError('These credentials did not work.');
        return;
      }
    } else if (!selectedCredentialId && credFormData.isNewCredential) {
      const { templates, items } = selectedConnection
        ? paging.connAuthById[selectedConnection]
        : {};
      const template = templates?.find((template) => template.id === this.state.selectedTemplate);
      const fields =
        templates?.length > 1 && template
          ? items.filter((item) => template.auth_parameters.includes(item.param_id))
          : items;
      const missingFields = getEmptyRequiredFields(fields, credFormData.selectedCredentialInfo);

      if (
        !has(credFormData.selectedCredentialInfo, 'cred_name') ||
        isNil(credFormData.selectedCredentialInfo.cred_name) ||
        isEmpty(credFormData.selectedCredentialInfo.cred_name)
      ) {
        missingFields.unshift({
          name: 'cred_name',
          display_name: 'Credential Name',
        });
      }

      if (!isEmpty(missingFields)) {
        let error = reduce(
          missingFields,
          (message, field) => `${message} ${field.display_name},`,
          'Please enter the following required fields: ',
        );
        error = error.substring(0, error.length - 1);
        onError(error);
        return;
      }

      this.setState({ awaitingProbeNewCred: true }, () => {
        if (quarantine.newCredentialId) {
          this.props.updateCredentialAndProbe(
            quarantine.newCredentialId,
            credFormData.selectedCredentialInfo,
            FORM_TYPE_EDIT_CREDENTIAL,
          );
        } else {
          this.props.addCredentialAndProbe(selectedConnection, credFormData.selectedCredentialInfo);
        }
      });
      return;
    }

    if (!selectedCredentialId) {
      onError('Please select a credential.');
      return;
    }

    onSuccess();
  }
  validateConfigure(data, onSuccess, onError) {
    if (!this.state.selectedBucket) {
      onError('Please select a destination folder.');
      return;
    }

    const { userId, isNew } = this.props;
    const quarantineConfig = {
      data_credentials: this.state.selectedCredentialId,
      config: pickBy(this.state.sourceConfig, identity),
    };

    this.props.saveUserQuarantine(userId, quarantineConfig, isNew);
    onSuccess();
  }
  buildSelectedBucket(config) {
    let path;

    if (config.path) {
      path = config.path;
    } else if (config.bucket) {
      path = config.bucket;

      if (config.prefix) {
        const separator = path.endsWith('/') || config.prefix.startsWith('/') ? '' : '/';
        path += `${separator}${config.prefix}`;
      }
    }

    return {
      id: path,
      path,
    };
  }
  handleConnectionSelected(selectedConnection) {
    this.setState({
      selectedConnection,
      selectedCredential: null,
      selectedCredentialId: null,
      credFormData: {},
      selectedBucket: null,
    });
  }
  handleCredSelectionChanged(selectedCredential) {
    this.setState({
      selectedCredential,
      selectedCredentialId: selectedCredential.id,
      selectedBucket: null,
    });
  }

  onChangeTemplate = (id) => {
    this.setState({ selectedTemplate: Number(id) });
  };

  handleCredentialFormInputChanged(input) {
    this.setState({
      credFormData: {
        ...this.state.credFormData,
        ...input,
      },
    });
  }
  handleSourceConfigChanged(newSourceConfig) {
    this.setState({
      sourceConfig: {
        ...this.state.sourceConfig,
        ...newSourceConfig,
      },
    });
  }
  onBucketSelected(selectedBucket) {
    const path = selectedBucket && selectedBucket.path;
    const sourceConfigWithBucket = assign({}, this.state.sourceConfig, {
      path,
    });

    this.setState({
      selectedBucket: selectedBucket,
      sourceConfig: sourceConfigWithBucket,
    });
  }
  onRequestProbeTree(isRoot = true, selectedFolder = null) {
    this.props.probeGetConnectionTree(this.state.selectedCredentialId, isRoot, selectedFolder);
  }
  onToggleNodeProbeTree(node) {
    this.props.probeToggleTreeNode(this.state.selectedCredentialId, node);
  }

  render() {
    if (this.state.redirectPath) {
      return <Redirect to={this.state.redirectPath} />;
    }

    const {
      paging,
      credentialsByType,
      credentialsById,
      vendors,
      isLoadingSaveCredential,
      isLoadingCredentials,
    } = this.props;
    const { selectedConnection, selectedCredentialId, selectedBucket, credFormData } = this.state;
    const { templates, items } = selectedConnection ? paging.connAuthById[selectedConnection] : {};
    const template = templates?.find((template) => template.id === this.state.selectedTemplate);
    const fields =
      templates?.length > 1 && template
        ? items.filter((item) => template.auth_parameters.includes(item.param_id))
        : items;
    const connectionAuthMeta = selectedConnection ? paging.connAuthMeta[selectedConnection] : null;
    let sourceConfigFields = paging.sourceConfigById
      ? selectedConnection
        ? paging.sourceConfigById[selectedConnection]
        : paging.sourceConfigById[0]
      : null;
    sourceConfigFields = filter(sourceConfigFields, (c) => c.name === 'start.cron').map((f) => ({
      ...f,
      inputType: 'frequency-type',
      display_name: 'Write files every',
    }));

    let errorConnectionsAllowed = filter(paging.sinksAllowed, (s) => {
      const meta = paging.metaInfoById[s];
      return (
        meta.connectionCategory === CONNECTION_CATEGORY_FILE ||
        (meta.connectionCategory === CONNECTION_CATEGORY_TEMPLATIZED_API &&
          meta.connectionTypeCategory === CONNECTION_CATEGORY_FILE)
      );
    });

    return (
      <Form
        title="Set a Destination"
        type={FORM_TYPE_EDIT_QUARANTINE}
        alwaysShowCancel={true}
        submitButtonText="Save"
        onSubmit={this.redirectAfterCompletion}
        onDismiss={this.redirectAfterCompletion}
      >
        <FormPage title="Select" validationMethod={this.validateSelectConnection}>
          <SelectConnection
            id={0}
            connectorResourceType={CONNECTORS_RESOURCE_TYPES.QUARANTINE}
            connectionMetas={paging.metaInfoById}
            connectionTypes={errorConnectionsAllowed}
            connectionsAllowed={errorConnectionsAllowed}
            selectedConnection={selectedConnection}
            onSelected={this.handleConnectionSelected}
            setContextualSupport={this.props.setContextualSupport}
            unsetContextualSupport={this.props.unsetContextualSupport}
            credentialsByType={this.props.credentialsByType}
          />
        </FormPage>
        <FormPage
          title="Authenticate"
          validationMethod={this.validateAuthenticate}
          showBackButton={true}
        >
          <AuthenticateDataSource
            id={1}
            connectionTypes={paging.sourcesSupported}
            connectionMetas={paging.metaInfoById}
            vendors={vendors}
            selectedConnection={selectedConnection}
            templates={templates}
            selectedTemplate={this.state.selectedTemplate}
            onChangeTemplate={this.onChangeTemplate}
            connectionAuthFields={fields}
            connectionAuthMeta={connectionAuthMeta}
            dataCredentials={credentialsByType}
            credentialsById={credentialsById}
            isFormAwaitingValidation={isLoadingSaveCredential}
            isLoadingData={isLoadingCredentials}
            onCredSelectionChanged={this.handleCredSelectionChanged}
            onFormInputChanged={this.handleCredentialFormInputChanged}
            onRefreshClicked={this.loadCredentials}
            onNewDropboxCredential={this.onNewDropboxCredential}
            onNewBoxCredential={this.onNewBoxCredential}
            credFormData={credFormData}
            setContextualSupport={this.props.setContextualSupport}
            unsetContextualSupport={this.props.unsetContextualSupport}
          />
        </FormPage>
        <FormPage title="Configure" validationMethod={this.validateConfigure} showBackButton={true}>
          <SelectBucketContainer
            id={2}
            selectedCredentialId={selectedCredentialId}
            selectedConnection={selectedConnection}
            connectionMetas={paging.metaInfoById}
            showFlowTriggers={false}
            sourceConfigFields={sourceConfigFields}
            sourceConfig={this.state.sourceConfig}
            onSourceConfigChanged={this.handleSourceConfigChanged}
            onSelected={this.onBucketSelected}
            hideMetaConfiguration
            isQuarantine
            onRequestProbeTree={this.onRequestProbeTree}
            onToggleNodeProbeTree={this.onToggleNodeProbeTree}
            selectedBucket={selectedBucket}
            resourceMode="quarantine"
            setContextualSupport={this.props.setContextualSupport}
            unsetContextualSupport={this.props.unsetContextualSupport}
          />
        </FormPage>
      </Form>
    );
  }
}

export default withSnackbar(EditQuarantineForm);
